import React, { useMemo, useState } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { setBranchId } from '../../../auth/actions';
import { Button, Input, Dropdown } from '../../../../common/ui';
import ShipmentTable from './ShipmentTable';
import FilterComp from './FilterComp';
import { setShipmentFilterVisibility } from '../actions';
import { IoCloseCircleOutline } from 'react-icons/io5';
import DateRangePicker from '../../../../common/components/DateRangePicker';

const getTodayDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const formatDate = (date) => date.toLocaleDateString('en-CA');

const Shipments = ({
  setBranchId,
  auth: { branchList, branchId },
  shipmentsState,
  setShipmentFilterVisibility,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [selectedTrackingNo, setSelectedTrackingNo] = useState(null);
  const [startDate, setStartDate] = useState(getTodayDate);
  const [endDate, setEndDate] = useState(getTodayDate);

  const fromDate = useMemo(() => formatDate(startDate), [startDate]);
  const toDate = useMemo(() => formatDate(endDate), [endDate]);

  const branchOptions = (branchList || []).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const handleDateRangeChange = (dateRange) => {
    if (dateRange?.startDate && dateRange?.endDate) {
      setStartDate(dateRange.startDate);
      setEndDate(dateRange.endDate);
      return;
    }

    const today = getTodayDate();
    setStartDate(today);
    setEndDate(today);
  };

  const handleResetFilters = () => {
    setFilters({});
    setSelectedTrackingNo(null);
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (key === 'trackingNo') {
      setSelectedTrackingNo(null);
    }
  };

  const generateFilterLabel = (key, value) => {
    switch (key) {
      case 'trackingNo':
        return `Tracking No: ${value?.trackingNo ?? value}`;
      default:
        return `${key}: ${value}`;
    }
  };

  const hasFilters = filters && Object.keys(filters).length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>Shipments</h1>
          <p className={styles.breadcrumb}>Delivery / Shipments</p>
        </div>
        {branchId != null && branchOptions.length > 0 && (
          <div className={styles.branchWrap}>
            <Dropdown
              label="Branch"
              options={branchOptions}
              value={branchId}
              onChange={handleChangeBranch}
              placeholder="Select branch"
            />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <Button onClick={() => {}}>New Shipment</Button>
            </div>
            <div className={styles.toolbarRight}>
              <div className={styles.dateRangeWrap}>
                <DateRangePicker
                  onDateRangeChange={handleDateRangeChange}
                  initialStartDate={startDate}
                  initialEndDate={endDate}
                />
              </div>
              <div className={styles.searchWrap}>
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(value) => setSearchTerm(value)}
                />
              </div>
              <Button
                variant="secondary"
                onClick={() =>
                  setShipmentFilterVisibility(!shipmentsState?.isShipmentFiltersOpen)
                }
              >
                Filters
              </Button>
            </div>
          </div>
          <div className={styles.filterTagsWrap}>
            {filters &&
              Object.entries(filters).map(([key, value]) => (
                <span key={key} className={styles.filterTag}>
                  {generateFilterLabel(key, value)}
                  <button
                    type="button"
                    className={styles.removeFilterBtn}
                    onClick={() => handleRemoveFilter(key)}
                    aria-label={`Remove ${key} filter`}
                  >
                    <IoCloseCircleOutline size={18} />
                  </button>
                </span>
              ))}
            {hasFilters && (
              <button
                type="button"
                className={styles.resetFiltersBtn}
                onClick={handleResetFilters}
              >
                Reset Filters
              </button>
            )}
          </div>
          <div className={styles.tableWrap}>
            <ShipmentTable
              filterParams={filters}
              searchTerm={searchTerm}
              fromDate={fromDate}
              toDate={toDate}
            />
          </div>
        </div>
      </div>
      <FilterComp
        filters={filters}
        setFilters={setFilters}
        selectedTrackingNo={selectedTrackingNo}
        SetSelectedTrackingNo={setSelectedTrackingNo}
      />
    </div>
  );
};

Shipments.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  shipmentsState: PropTypes.object.isRequired,
  setShipmentFilterVisibility: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  shipmentsState: state.shipmentsState,
});

export default connect(mapStateToProps, {
  setBranchId,
  setShipmentFilterVisibility,
})(Shipments);
